import React from "react";
import { Star } from "@phosphor-icons/react";

/**
 * Auto-scrolling testimonial marquee for the landing page.
 * Two rows scroll in opposite directions using a pure-CSS animation.
 * Duplicated array so the loop is seamless (no visible restart snap).
 *
 * To swap for real user testimonials later: replace the TESTIMONIALS array
 * with { quote, name, role, handle?, result? } objects. Anything more than
 * 8 items looks great; 4 is the minimum for a good loop.
 */
const TESTIMONIALS = [
  {
    quote: "I created 12 branded videos in one afternoon and stayed consistent for the entire month. Insane.",
    name: "Jasmine T.",
    role: "Brand Strategist",
    result: "12 videos / 1 afternoon",
  },
  {
    quote: "The AI Twin feature is unreal. My audience can't tell the difference — I'm posting 3× more and engagement is way up.",
    name: "Marcus D.",
    role: "Digital Creator",
    result: "3× post volume",
  },
  {
    quote: "I was drowning in content. Now I batch a whole month in one sitting. The Pipeline keeps me on track.",
    name: "Aisha R.",
    role: "Coach &amp; Speaker",
    result: "Batches monthly",
  },
  {
    quote: "The LinkedIn Engine alone is worth it. My posts went from 50 impressions to 5,000+. Hook library is fire.",
    name: "Devon W.",
    role: "E-commerce Founder",
    result: "100× LinkedIn reach",
  },
  {
    quote: "We onboarded 8 clients onto the platform. Each has their own brand voice, colors, style. Team output tripled.",
    name: "Priya K.",
    role: "Agency Owner",
    result: "8 clients / 1 platform",
  },
  {
    quote: "The lifestyle videos look like I hired a whole production team. The outfit swap? Chef's kiss.",
    name: "Carlos M.",
    role: "Fitness Creator",
    result: "Studio-quality output",
  },
  {
    quote: "As a realtor, I had zero time to film. Now I show up on IG daily and my listings are booking faster than ever.",
    name: "Renée G.",
    role: "Realtor",
    result: "Daily on IG",
  },
  {
    quote: "I finally launched my course after 2 years of stalling. Series Studio wrote every lesson script in an afternoon.",
    name: "Kaleb S.",
    role: "Course Creator",
    result: "Course shipped",
  },
];

// Split into two rows for the up/down opposing scroll.
const ROW_A = TESTIMONIALS.slice(0, 4);
const ROW_B = TESTIMONIALS.slice(4);

function Card({ t }) {
  return (
    <div className="min-w-[300px] max-w-[300px] p-5 rounded-2xl bg-white border border-zinc-100 shadow-sm mx-2 flex-shrink-0" data-testid="testimonial-card">
      <div className="flex gap-0.5 mb-2">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={12} weight="fill" className="text-amber-400" />
        ))}
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed line-clamp-4 mb-3" dangerouslySetInnerHTML={{ __html: `&ldquo;${t.quote}&rdquo;` }} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E91E8C] to-pink-400 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
            {t.name.split(" ").map((n) => n[0]).join("")}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-zinc-900 truncate" dangerouslySetInnerHTML={{ __html: t.name }} />
            <p className="text-[10px] text-zinc-500 truncate" dangerouslySetInnerHTML={{ __html: t.role }} />
          </div>
        </div>
        {t.result && (
          <span className="text-[9px] font-bold px-2 py-1 rounded-full bg-[#E91E8C]/10 text-[#E91E8C] flex-shrink-0">
            {t.result}
          </span>
        )}
      </div>
    </div>
  );
}

export default function TestimonialMarquee() {
  return (
    <section className="py-14 overflow-hidden relative" data-testid="testimonial-marquee">
      <div className="max-w-5xl mx-auto px-4 mb-8 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Real creators, real momentum</p>
        <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900">
          People are shipping content again — <span className="text-[#E91E8C]">without burning out</span>.
        </h2>
      </div>

      {/* Row A — scroll left */}
      <div className="flex overflow-hidden mask-fade">
        <div className="flex animate-marquee-a" style={{ animationDuration: "45s" }}>
          {[...ROW_A, ...ROW_A, ...ROW_A].map((t, i) => <Card key={`a-${i}`} t={t} />)}
        </div>
      </div>

      {/* Row B — scroll right */}
      <div className="flex overflow-hidden mask-fade mt-3">
        <div className="flex animate-marquee-b" style={{ animationDuration: "55s" }}>
          {[...ROW_B, ...ROW_B, ...ROW_B].map((t, i) => <Card key={`b-${i}`} t={t} />)}
        </div>
      </div>

      {/* Inline keyframes + mask so we don't have to touch tailwind config */}
      <style>{`
        @keyframes marquee-a {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-320px * 4)); }
        }
        @keyframes marquee-b {
          from { transform: translateX(calc(-320px * 4)); }
          to   { transform: translateX(0); }
        }
        .animate-marquee-a { animation: marquee-a linear infinite; }
        .animate-marquee-b { animation: marquee-b linear infinite; }
        .animate-marquee-a:hover, .animate-marquee-b:hover { animation-play-state: paused; }
        .mask-fade {
          mask-image: linear-gradient(90deg, transparent 0, black 8%, black 92%, transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0, black 8%, black 92%, transparent 100%);
        }
      `}</style>
    </section>
  );
}
